import { Radio, Spin } from 'antd';
import { Controller, type UseFormReturn, useWatch } from 'react-hook-form';

import {
  Button,
  Input,
  MaskedInput,
  SecurityStatusBadge
} from '@/components/Shared/ui';
import { PHONE_NUMBER_REGEX, SecurityStatus } from '@/constants';
import { LoadingOutlined } from '@/media/antd-icons';
import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import SmsMessageIcon from '@/media/icons/sms-message.svg?react';
import WhatsappIcon from '@/media/icons/whatsapp.svg?react';
import { isSecurityStatus } from '@/services';
import { surroundWithQuotes } from '@/services/helpers/strings';

import type { ClientFormValues } from '../../types/client-form';

const options = [
  {
    label: (
      <>
        <WhatsappIcon />
        Whatsapp
      </>
    ),
    value: 'WhatsApp'
  },
  {
    label: (
      <>
        <SmsMessageIcon />
        SMS
      </>
    ),
    value: 'SMS'
  }
];

interface IProps {
  clientComments: string[];
  clientError: string;
  clientFormLoading: boolean;
  clientSecurity: {
    statusCode: string | null;
    comment: string | null;
  };
  handleClientFormSubmit: (values: ClientFormValues) => Promise<void>;
  handleClose: () => void;
  hasWhatsApp: boolean;
  methods: UseFormReturn<ClientFormValues, unknown, ClientFormValues>;
  onOpenComments: () => void;
  searchClientLoading: boolean;
}

const ClientForm: React.FC<IProps> = ({
  clientComments,
  clientError,
  clientFormLoading,
  clientSecurity,
  handleClientFormSubmit,
  handleClose,
  hasWhatsApp,
  methods,
  onOpenComments,
  searchClientLoading
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit
  } = methods;

  const phone = useWatch({ control, name: 'phone' });

  return (
    <form
      className="client-otp-modal__form"
      onSubmit={handleSubmit(handleClientFormSubmit)}
    >
      <Controller
        control={control}
        name="iin"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 12, message: 'Неверный ИИН' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            label="ИИН клиента"
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length > 12) return;
              field.onChange(value);
            }}
            error={errors.iin?.message}
            endContent={
              searchClientLoading ? (
                <span className="client-otp-modal__iin-loading">
                  <Spin indicator={<LoadingOutlined />} />
                </span>
              ) : undefined
            }
            isRequired
          />
        )}
      />

      {!!clientSecurity.statusCode &&
        isSecurityStatus(clientSecurity.statusCode) &&
        clientSecurity.statusCode !== SecurityStatus.notVerified && (
          <div
            className={`client-otp-modal__security-status client-otp-modal__security-status--${clientSecurity.statusCode}`}
          >
            <h5 className="client-otp-modal__security-status-title">
              Статус безопасности клиента
            </h5>
            <SecurityStatusBadge statusCode={clientSecurity.statusCode} />
            {!!clientSecurity.comment && (
              <i className="client-otp-modal__security-status-comment">
                Комментарий: {surroundWithQuotes(clientSecurity.comment)}
              </i>
            )}
          </div>
        )}

      {clientComments.length > 0 && (
        <Button size="small" onClick={onOpenComments} type="button">
          По данному клиенту есть комментарии ({clientComments.length})
        </Button>
      )}

      <Controller
        control={control}
        name="fullName"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            label="ФИО клиента"
            value={field.value ?? ''}
            error={errors.fullName?.message}
            isRequired
          />
        )}
      />
      <Controller
        control={control}
        name="phone"
        rules={{
          required: 'Пожалуйста, введите данные',
          validate: (value) => {
            if (!value.match(PHONE_NUMBER_REGEX))
              return 'Неверный номер телефона';
          }
        }}
        render={({ field }) => (
          <MaskedInput
            {...field}
            type="tel"
            label="Номер телефона"
            value={field.value ?? ''}
            error={errors.phone?.message}
            mask="+7 (___) ___-__-__"
            isRequired
            autoComplete="off"
          />
        )}
      />

      {phone.length > 0 && !errors.phone && hasWhatsApp && (
        <div className="client-otp-modal__sent-with">
          <p className="client-otp-modal__sent-text">
            Отправить код верификации через:
          </p>
          <Controller
            control={control}
            name="sentWithType"
            render={({ field }) => (
              <Radio.Group block options={options} value={field.value} />
            )}
          />
        </div>
      )}

      {clientError && (
        <p className="client-otp-modal__error">
          <AlertCircleIcon />
          {clientError}
        </p>
      )}

      <div className="client-otp-modal__actions">
        <Button type="button" onClick={handleClose}>
          Отмена
        </Button>
        <Button
          disabled={Object.values(errors).length !== 0}
          variant="filled-red"
          isLoading={clientFormLoading}
        >
          Далее
        </Button>
      </div>
    </form>
  );
};

export default ClientForm;
